import type { Board } from 'chessboard/model/board/types';
import { Core } from 'chessboard/core/config/types';
import type { Renderer } from 'chessboard/renderers/config/types';
import { PointerEvents, Pointermove, Pointerup } from 'chessboard/model/dom/get-pointer-events';
import { createCoreEvent } from 'chessboard/core/model/create-core-event';
import { getKeyPressed } from 'chessboard/model/board/get-key-pressed';
import { isRightButton } from 'chessboard/model/dom/is-right-button';
import { onNextTick } from 'utils/on-next-tick';
import { debounceAnimationFrame } from 'utils/debounce';
import type { API } from 'chessboard/api/config/types';

export type ClientPointerEvent = MouseEvent | PointerEvent | TouchEvent;

type UserEventHandler = (event: ClientPointerEvent) => void;

export interface UserEventHandlers {
  handleDocumentPointerdown: UserEventHandler;
  handlePointerdown: UserEventHandler;
  handlePointermove: UserEventHandler;
  handlePointerup: UserEventHandler;
}

/**
 * Create handlers for user events.
 */
export function createUserEventHandlers({
  el,
  getPointerPosition,
  options,
  run,
  api,
  monitorUntrustedEvents,
}: {
  el: HTMLElement;
  api: API.Extended;
  getPointerPosition: Renderer.Model['getPointerPosition'];
  options: Board.Options;
  run: (event: Core.Event) => void;
  monitorUntrustedEvents: boolean;
}): UserEventHandlers {
  const handlePointermove = debounceAnimationFrame(runPointerMoveEvent);

  return {
    handleDocumentPointerdown: createTrustedEventHandler(handleDocumentPointerdown),
    handlePointerdown: createTrustedEventHandler(handlePointerdown),
    handlePointermove: createTrustedEventHandler(handlePointermove),
    handlePointerup: createTrustedEventHandler(handlePointerup),
  };

  /**
   * Watch for instances of event.isTrusted === false.
   */
  function createTrustedEventHandler(handler: UserEventHandler) {
    return function (e: ClientPointerEvent) {
      handler(e);

      if (!e.isTrusted && monitorUntrustedEvents) {
        const UntrustedEvent = createCoreEvent(Core.FairPlay.UntrustedUserEvent, e);
        api.emit(Core.FairPlay.UntrustedUserEvent, UntrustedEvent);
      }
    };
  }

  /**
   * Handle a document pointerdown event.
   */
  function handleDocumentPointerdown(event: ClientPointerEvent): void {
    const eventType = isRightButton(event)
      ? Core.UserEvents.PointerdownRight
      : Core.UserEvents.PointerdownLeft;

    const Event = createCoreEvent(eventType);

    Event.data = {
      square: null,
    };

    return run(Event);
  }

  /**
   * Handle a pointerdown event.
   */
  function handlePointerdown(event: ClientPointerEvent): void {
    event.stopPropagation();

    document.addEventListener(Pointermove, handlePointermove);
    document.addEventListener(Pointerup, handlePointerup);

    const eventType = isRightButton(event)
      ? Core.UserEvents.PointerdownRight
      : Core.UserEvents.PointerdownLeft;

    const Event = createCoreEvent(eventType);

    Event.data = {
      ...getPointerPosition({ el, event, flipped: options.flipped }),
      keyPressed: getKeyPressed(event),
      timestamp: performance.now(),
    };

    onNextTick(function addMouseDownListener() {
      document.addEventListener(PointerEvents.Mousedown, handleMousedown, { once: true });
    });

    return run(Event);
  }

  /**
   * Handle a mouse down event.
   */
  function handleMousedown(event: ClientPointerEvent): void {
    const eventType = isRightButton(event)
      ? Core.UserEvents.MousedownRight
      : Core.UserEvents.MousedownLeft;

    const Event = createCoreEvent(eventType);

    Event.data = {
      ...getPointerPosition({ el, event, flipped: options.flipped }),
      keyPressed: getKeyPressed(event),
      timestamp: performance.now(),
    };

    return run(Event);
  }

  /**
   * Run pointermove event.
   */
  function runPointerMoveEvent(event: ClientPointerEvent): void {
    const Event = createCoreEvent(Core.UserEvents.Pointermove);

    Event.data = {
      ...getPointerPosition({ el, event, flipped: options.flipped }),
      keyPressed: getKeyPressed(event),
      timestamp: performance.now(),
    };

    run(Event);
  }

  /**
   * Handle a pointerup event.
   */
  function handlePointerup(event: ClientPointerEvent): void {
    document.removeEventListener(PointerEvents.Mousedown, handleMousedown);
    document.removeEventListener(Pointermove, handlePointermove);
    document.removeEventListener(Pointerup, handlePointerup);

    const Event = createCoreEvent(Core.UserEvents.Pointerup);

    Event.data = {
      ...getPointerPosition({ el, event, flipped: options.flipped }),
      keyPressed: getKeyPressed(event),
      timestamp: performance.now(),
    };

    return run(Event);
  }
}
